import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { getCurrentUserOrganizationId } from './referenceDataCache';
import { eventBus } from '../events/eventBus';
import type { ClientTag, ClientTagFormData, ClientTagAssignment } from '../types';

interface ClientTagStore {
  // State
  tags: ClientTag[];
  assignments: ClientTagAssignment[];
  loading: boolean;
  // true solo dopo un fetch andato a buon fine (per il pruning dei filtri:
  // distingue lista vuota reale da lista non ancora caricata/fetch fallita)
  fetched: boolean;
  error: string | null;

  // Actions for managing tags
  fetchTags: () => Promise<void>;
  createTag: (tag: ClientTagFormData) => Promise<ClientTag | null>;
  updateTag: (id: string, tag: Partial<ClientTagFormData>) => Promise<ClientTag | null>;
  deleteTag: (id: string) => Promise<boolean>;
  reorderTags: (tagIds: string[]) => Promise<boolean>;

  // Actions for managing tag assignments
  assignTagsToClient: (clientId: string, tagIds: string[]) => Promise<boolean>;
  removeTagFromClient: (clientId: string, tagId: string) => Promise<boolean>;
  getClientTags: (clientId: string) => Promise<ClientTag[]>;
  getTaggedClients: (tagId: string) => Promise<string[]>;

  // Utility actions
  clearError: () => void;
}

export const useClientTagStore = create<ClientTagStore>((set, get) => ({
  // Initial state
  tags: [],
  assignments: [],
  loading: false,
  fetched: false,
  error: null,

  // Fetch all tags for the current organization
  fetchTags: async () => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization ID from cache
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) throw new Error('User not associated with an organization');

      const { data, error } = await supabase
        .from('client_tags')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true});

      if (error) throw error;
      set({ tags: data || [], fetched: true });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  // Create a new tag
  createTag: async (tagData: ClientTagFormData) => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization ID
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Get the next sort order
      const { data: maxOrderData } = await supabase
        .from('client_tags')
        .select('sort_order')
        .eq('organization_id', userProfile.organization_id)
        .order('sort_order', { ascending: false })
        .limit(1)
        .maybeSingle();

      const nextSortOrder = (maxOrderData?.sort_order || 0) + 1;

      const { data, error } = await supabase
        .from('client_tags')
        .insert({
          organization_id: userProfile.organization_id,
          name: tagData.name,
          description: tagData.description || null,
          color: tagData.color,
          sort_order: tagData.sort_order || nextSortOrder,
          is_active: tagData.is_active !== false,
          created_by: userData.user.id
        })
        .select()
        .single();

      if (error) throw error;

      // Update local state
      const tags = get().tags;
      set({ tags: [...tags, data].sort((a, b) => a.sort_order - b.sort_order) });
      
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    } finally {
      set({ loading: false });
    }
  },

  // Update an existing tag
  updateTag: async (id: string, tagData: Partial<ClientTagFormData>) => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('client_tags')
        .update({
          ...tagData,
          updated_by: userData.user.id
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      const tags = get().tags.map(tag => tag.id === id ? data : tag);
      set({ tags: tags.sort((a, b) => a.sort_order - b.sort_order) });
      
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    } finally {
      set({ loading: false });
    }
  },

  // Delete a tag
  deleteTag: async (id: string) => {
    set({ loading: true, error: null });
    try {
      // First, remove all assignments for this tag
      const { error: assignmentError } = await supabase
        .from('client_tag_assignments')
        .delete()
        .eq('tag_id', id);

      if (assignmentError) throw assignmentError;

      // Then delete the tag itself
      const { error } = await supabase
        .from('client_tags')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update local state
      const tags = get().tags.filter(tag => tag.id !== id);
      set({ tags });
      
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  // Reorder tags
  reorderTags: async (tagIds: string[]) => {
    set({ loading: true, error: null });
    try {
      // Update sort_order for each tag
      const updates = tagIds.map((tagId, index) => 
        supabase
          .from('client_tags')
          .update({ sort_order: index + 1 })
          .eq('id', tagId)
      );

      const results = await Promise.all(updates);
      
      // Check for errors
      const errors = results.filter(result => result.error);
      if (errors.length > 0) {
        throw new Error(`Failed to reorder tags: ${errors[0].error?.message}`);
      }

      // Refresh tags to get updated order
      await get().fetchTags();
      
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  // Assign multiple tags to a client
  assignTagsToClient: async (clientId: string, tagIds: string[]) => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization ID
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Fetch existing assignments to compute diff (preserves assigned_at)
      const { data: existing } = await supabase
        .from('client_tag_assignments')
        .select('tag_id')
        .eq('client_id', clientId);

      const existingTagIds = (existing || []).map(a => a.tag_id);
      const toAdd = tagIds.filter(id => !existingTagIds.includes(id));
      const toRemove = existingTagIds.filter(id => !tagIds.includes(id));

      // Delete only removed tags
      if (toRemove.length > 0) {
        const { error: deleteError } = await supabase
          .from('client_tag_assignments')
          .delete()
          .eq('client_id', clientId)
          .in('tag_id', toRemove);

        if (deleteError) throw deleteError;
      }

      // Insert only new tags
      if (toAdd.length > 0) {
        const assignments = toAdd.map(tagId => ({
          organization_id: userProfile.organization_id,
          client_id: clientId,
          tag_id: tagId,
          assigned_by: userData.user.id
        }));

        const { error: insertError } = await supabase
          .from('client_tag_assignments')
          .insert(assignments);

        if (insertError) throw insertError;
      }

      // Emit workflow trigger events for tag changes
      // Determine if client is a lead or a client by checking client_states
      try {
        const { data: clientData } = await supabase
          .from('clients')
          .select('id, stato_cliente, business_name, contact_name, email, phone')
          .eq('id', clientId)
          .single();

        if (clientData) {
          const { data: stateData } = await supabase
            .from('client_states')
            .select('is_lead')
            .eq('id', clientData.stato_cliente)
            .single();

          const entityPrefix = stateData?.is_lead ? 'lead' : 'client';

          for (const tagId of toAdd) {
            eventBus.emitEvent({ type: `${entityPrefix}:tag_added`, payload: {
              clientId, tagId, organizationId: userProfile.organization_id,
              businessName: clientData.business_name, contactName: clientData.contact_name,
              email: clientData.email, phone: clientData.phone,
            }} as any);
          }
          for (const tagId of toRemove) {
            eventBus.emitEvent({ type: `${entityPrefix}:tag_removed`, payload: {
              clientId, tagId, organizationId: userProfile.organization_id,
              businessName: clientData.business_name, contactName: clientData.contact_name,
              email: clientData.email, phone: clientData.phone,
            }} as any);
          }
        }
      } catch (e) {
        console.warn('[ClientTagStore] Failed to emit tag workflow events:', e);
      }

      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  // Remove a specific tag from a client
  removeTagFromClient: async (clientId: string, tagId: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('client_tag_assignments')
        .delete()
        .eq('client_id', clientId)
        .eq('tag_id', tagId);

      if (error) throw error;

      // Emit workflow trigger event
      try {
        const { data: clientData } = await supabase
          .from('clients')
          .select('stato_cliente, business_name, contact_name, email, phone')
          .eq('id', clientId)
          .single();
        if (clientData) {
          const { data: stateData } = await supabase.from('client_states').select('is_lead').eq('id', clientData.stato_cliente).single();
          const { data: up } = await supabase.from('user_profiles').select('organization_id').eq('id', (await supabase.auth.getUser()).data.user?.id || '').single();
          const entityPrefix = stateData?.is_lead ? 'lead' : 'client';
          eventBus.emitEvent({ type: `${entityPrefix}:tag_removed`, payload: {
            clientId, tagId, organizationId: up?.organization_id,
            businessName: clientData.business_name, contactName: clientData.contact_name,
            email: clientData.email, phone: clientData.phone,
          }} as any);
        }
      } catch (e) { console.warn('[ClientTagStore] Failed to emit tag removed event:', e); }

      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  // Get all tags assigned to a specific client
  getClientTags: async (clientId: string) => {
    try {
      const { data, error } = await supabase
        .from('client_tag_assignments')
        .select(`
          tag_id,
          assigned_at,
          client_tags!inner (
            id,
            name,
            description,
            color,
            sort_order,
            is_active
          )
        `)
        .eq('client_id', clientId)
        .eq('client_tags.is_active', true)
        .order('client_tags.sort_order', { ascending: true });

      if (error) throw error;

      return data?.map(assignment => ({ ...assignment.client_tags, assigned_at: assignment.assigned_at })).filter(Boolean) || [];
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return [];
    }
  },

  // Get all clients with a specific tag
  getTaggedClients: async (tagId: string) => {
    try {
      const { data, error } = await supabase
        .from('client_tag_assignments')
        .select('client_id')
        .eq('tag_id', tagId);

      if (error) throw error;
      return data?.map(assignment => assignment.client_id) || [];
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return [];
    }
  },

  // Clear error state
  clearError: () => set({ error: null })
}));